import { COVER_CROP_FIT_REASON, COVER_CROP_FIT_STATUS, CoverCropFitReason } from "consts";

import { SilverCoverCropSowingRule, SilverCropCalendar, SoilSightCoverCropTiming } from "models";

import { CropYear } from "pages/app/scenario-analysis/types/scenario";

type CoverCropFitBase = {
  minimumDurationMonths: number;
  /** The crop harvested before this row's cover crop goes in. */
  previousCropId: string;
  /** 1-based year of that crop; year 1's cover crop follows the last year's crop. */
  previousCropYearNumber: number;
  isPreviousCropLastYear: boolean;
};

export type CoverCropFit =
  | (CoverCropFitBase & { status: typeof COVER_CROP_FIT_STATUS.FITS })
  | (CoverCropFitBase & {
      status: typeof COVER_CROP_FIT_STATUS.WONT_FIT;
      reason: CoverCropFitReason;
      /** True when a spring-sown crop in this row would give the mix room to be sown. */
      wouldFitBeforeSpringCrop: boolean;
    });

const MONTHS_PER_YEAR = 12;

// Months are counted from January of the previous crop's harvest year: index 0 = January, 12 = next January.
const monthIndex = (yearOffset: number, month: number) => yearOffset * MONTHS_PER_YEAR + month - 1;
const yearOffsetOf = (index: number) => Math.floor(index / MONTHS_PER_YEAR);
const monthOf = (index: number) => (index % MONTHS_PER_YEAR) + 1;

// Silver's main-crop sowing: its start month, later if the field frees up within the grace months, else next year.
const nextCropSowingIndex = (fieldFreeIndex: number, nextCrop: SilverCropCalendar, lateSowingGraceMonths: number) => {
  const fieldFreeYear = yearOffsetOf(fieldFreeIndex);
  const latestSowingMonth = Math.max(nextCrop.latestSowingMonth, nextCrop.sowingStartMonth);
  if (monthOf(fieldFreeIndex) <= latestSowingMonth + lateSowingGraceMonths) {
    return Math.max(fieldFreeIndex, monthIndex(fieldFreeYear, nextCrop.sowingStartMonth));
  }
  return monthIndex(fieldFreeYear + 1, nextCrop.sowingStartMonth);
};

// Mirrors Silver's scheduleCoverCrop; returns why the cover would be skipped, or null when it is sown.
const getCoverCropSkipReason = (
  rule: SilverCoverCropSowingRule,
  previousCrop: SilverCropCalendar,
  nextCrop: SilverCropCalendar,
  lateSowingGraceMonths: number
): CoverCropFitReason | null => {
  // Winter mixes get a later window before a spring-sown crop; other mixes have no such window.
  const [windowOpenMonth, windowCloseMonth] =
    nextCrop.isSpringSown && rule.sowingStartMonthBeforeSpringCrop !== null && rule.latestSowingMonthBeforeSpringCrop !== null
      ? [rule.sowingStartMonthBeforeSpringCrop, rule.latestSowingMonthBeforeSpringCrop]
      : [rule.sowingStartMonth, rule.latestSowingMonth];

  const fieldFreeIndex = monthIndex(0, previousCrop.harvestMonth) + 1;
  const sowingYear = yearOffsetOf(fieldFreeIndex);
  if (fieldFreeIndex > monthIndex(sowingYear, windowCloseMonth)) return COVER_CROP_FIT_REASON.SOWING_WINDOW_CLOSED;

  const coverSowingIndex = Math.max(fieldFreeIndex, monthIndex(sowingYear, windowOpenMonth));
  const coverEndIndex = nextCropSowingIndex(fieldFreeIndex, nextCrop, lateSowingGraceMonths) - Math.max(rule.terminationOffsetMonths, 0);
  // Covers both "no gap at all" (end before sowing) and a gap shorter than the minimum.
  if (coverEndIndex - coverSowingIndex + 1 < rule.minimumDurationMonths) return COVER_CROP_FIT_REASON.SHORT_WINDOW;

  return null;
};

/** Checks each year's cover crop against the previous crop's harvest (blank rows skipped, as in the API). */
export const getCoverCropFitByYearId = (years: CropYear[], coverCropTiming: SoilSightCoverCropTiming): Record<string, CoverCropFit> => {
  const fitByYearId: Record<string, CoverCropFit> = {};
  const cropRowIndexes = years.flatMap((year, index) => (year.crop ? [index] : []));

  cropRowIndexes.forEach((rowIndex, position) => {
    const year = years[rowIndex];
    const rule = year.coverCropId ? coverCropTiming.coverCropRules[year.coverCropId] : undefined;
    if (!rule) return;

    const isPreviousCropLastYear = position === 0;
    const previousRowIndex = cropRowIndexes[isPreviousCropLastYear ? cropRowIndexes.length - 1 : position - 1];
    const previousCropId = years[previousRowIndex].crop;
    const previousCrop = coverCropTiming.cropCalendar[previousCropId];
    const nextCrop = coverCropTiming.cropCalendar[year.crop];
    if (!previousCrop || !nextCrop) return;

    const fitBase = { minimumDurationMonths: rule.minimumDurationMonths, previousCropId, previousCropYearNumber: previousRowIndex + 1, isPreviousCropLastYear };
    const skipReason = getCoverCropSkipReason(rule, previousCrop, nextCrop, coverCropTiming.lateSowingGraceMonths);
    if (!skipReason) {
      fitByYearId[year.id] = { ...fitBase, status: COVER_CROP_FIT_STATUS.FITS };
      return;
    }

    // Only suggest a spring crop when one in the region's calendar would actually let this mix be sown.
    const wouldFitBeforeSpringCrop =
      !nextCrop.isSpringSown &&
      Object.values(coverCropTiming.cropCalendar).some(
        (crop) => crop.isSpringSown && getCoverCropSkipReason(rule, previousCrop, crop, coverCropTiming.lateSowingGraceMonths) === null
      );
    fitByYearId[year.id] = { ...fitBase, status: COVER_CROP_FIT_STATUS.WONT_FIT, reason: skipReason, wouldFitBeforeSpringCrop };
  });

  return fitByYearId;
};
